import {
  Badge, Button, Card, CareDay, DataTable, DateInput, ErrorState, LARGEST_PAGE, PageHeader, Row, Select, Stack,
  todayIso, useApi, useI18n, useMemberSwitch,
  type ClassDaySummaryResponse,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * The administration's view of daily care (GAPS 3.1): a class's board for a date, and any child's day opened from it to
 * read, correct and, where the nursery reviews days, publish, by the same rules as the staff app (`CareDay`).
 *
 * The board is a staff board, so the classes offered are the ones the API lets the caller act on, and a day of an
 * earlier academic year answers with its refusal (Q33).
 */
export function DailyCare() {
  const api = useApi();
  const { t, formatTime, settings } = useI18n();
  const reviewRequired = useMemberSwitch('daily.review_required');
  const [classId, setClassId] = useState<number | ''>('');
  const [date, setDate] = useState(todayIso(settings));
  const [open, setOpen] = useState<number | null>(null);

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active', pageSize: LARGEST_PAGE }],
    queryFn: () => api.classes.list({ status: 'Active', pageSize: LARGEST_PAGE }),
  });
  const reachable = (classes.data?.items ?? []).filter((klass) => klass.inStaffReach);
  const active = classId === '' ? reachable[0]?.id : classId;

  const board = useQuery({
    queryKey: ['class-day', active, date],
    queryFn: () => api.daily.forClass(active!, date),
    enabled: active !== undefined && date !== '',
  });

  const name = (row: ClassDaySummaryResponse) => row.preferredName ?? `${row.firstName} ${row.lastName}`;

  return (
    <Stack>
      <PageHeader title={t('daily.title')} lede={t('daily.lede')} />

      <Row className="dg-row--wrap">
        <Select
          aria-label={t('class.one')}
          value={active ?? ''}
          onChange={(event) => { setClassId(event.target.value === '' ? '' : Number(event.target.value)); setOpen(null); }}
        >
          {reachable.map((klass) => <option key={klass.id} value={klass.id}>{klass.name}</option>)}
        </Select>
        <DateInput aria-label={t('audit.when')} value={date} max={todayIso(settings)} onChange={(value) => { setDate(value); setOpen(null); }} />
      </Row>

      {classes.isError && <ErrorState error={classes.error} onRetry={() => void classes.refetch()} />}

      {open !== null && (
        <Stack>
          <Row><Button variant="ghost" onClick={() => setOpen(null)}>{t('action.back')}</Button></Row>
          <CareDay childId={open} date={date} />
        </Stack>
      )}

      {open === null && (
        <Card flush>
          <DataTable
            caption={t('daily.title')}
            rows={board.data}
            rowKey={(row) => row.childId}
            isLoading={board.isPending && active !== undefined}
            error={board.error}
            onRetry={() => void board.refetch()}
            empty={t('class.empty')}
            columns={[
              { key: 'child', header: t('child.one'), render: (row) => name(row) },
              {
                key: 'recorded',
                header: t('daily.recorded'),
                render: (row) => (
                  <Row className="dg-row--wrap">
                    {row.asleepSince && <Badge tone="info">{t('daily.asleep', { time: formatTime(row.asleepSince) })}</Badge>}
                    {row.recordedKinds.map((kind) => <Badge key={kind} tone="success">{t(`care.kind.${kind}`)}</Badge>)}
                    {row.missingKinds.map((kind) => <Badge key={kind} tone="neutral">{t(`care.kind.${kind}`)}</Badge>)}
                  </Row>
                ),
              },
              {
                key: 'status',
                header: t('audit.action'),
                render: (row) => (reviewRequired && row.status === 'Draft' && row.recordedKinds.length > 0
                  ? <Badge tone="warning">{t('careDay.draft')}</Badge>
                  : null),
              },
              {
                key: 'open',
                header: '',
                render: (row) => (
                  <Button variant="ghost" aria-label={t('daily.openOf', { name: name(row) })} onClick={() => setOpen(row.childId)}>
                    {t('daily.open')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}
    </Stack>
  );
}
